import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import type { SensorData } from './analysisTabSensorTypes';

export interface ScaleGroup {
  scaleKey: string;
  units: string[];
  min: number | undefined;
  max: number | undefined;
  hasCustomScaling: boolean;
  hasRelaySensor: boolean;
  representativeUnit: string;
}

const RELAY_KEYWORDS_FOR_SCALING = ['relay', 'acid', 'pump', 'valve', 'switch', 'alarm'];

function sensorNameHasRelayKeyword(sensorName: string, keywords: readonly string[]): boolean {
  const lowerName = sensorName.toLowerCase();
  for (const keyword of keywords) {
    if (lowerName.includes(keyword.toLowerCase())) {
      return true;
    }
  }
  return false;
}

function getUniqueSensorValues(dataPoints: { value: number }[]): number[] {
  const values = new Set<number>();
  for (const point of dataPoints) {
    values.add(point.value);
  }
  return Array.from(values);
}

function isBinaryOrStepData(uniqueValues: number[]): { isBinaryData: boolean; isStepData: boolean } {
  let isBinaryData = true;
  let isStepData = uniqueValues.length <= 3;
  for (const val of uniqueValues) {
    if (val !== 0 && val !== 1) {
      isBinaryData = false;
    }
    if (val < 0 || val > 1.5) {
      isStepData = false;
    }
  }
  return { isBinaryData, isStepData };
}

function hasNoMeaningfulUnit(unit?: string): boolean {
  if (!unit || unit.trim() === '') {
    return true;
  }
  const lowerUnit = unit.toLowerCase();
  return lowerUnit.includes('state') || lowerUnit.includes('status');
}

function isRelaySensorForScaling(sensorName: string, sensorData?: SensorData): boolean {
  const hasRelayKeyword = sensorNameHasRelayKeyword(sensorName, RELAY_KEYWORDS_FOR_SCALING);
  if (!sensorData?.dataPoints?.length) {
    return hasRelayKeyword;
  }

  const uniqueValues = getUniqueSensorValues(sensorData.dataPoints);
  const { isBinaryData, isStepData } = isBinaryOrStepData(uniqueValues);
  const noMeaningfulUnit = hasNoMeaningfulUnit(sensorData.unit);

  return hasRelayKeyword || (isBinaryData && noMeaningfulUnit) || (isStepData && noMeaningfulUnit);
}

function getSensorScaleKey(sensor: SensorData): string {
  const unit = sensor.unit?.trim() || '';
  return unit === '' ? sensor.sensorName : unit;
}

function findUomRange(
  uomRanges: { uom: string; minValue: number; maxValue: number }[],
  unitToMatch: string
): { uom: string; minValue: number; maxValue: number } | undefined {
  for (const range of uomRanges) {
    if (range.uom === unitToMatch) {
      return range;
    }
  }
  return undefined;
}

function unitHasRelaySensor(unit: string, visibleSensorsWithData: SensorData[]): boolean {
  for (const sensor of visibleSensorsWithData) {
    if (getSensorScaleKey(sensor) === unit && isRelaySensorForScaling(sensor.sensorName, sensor)) {
      return true;
    }
  }
  return false;
}

function getEffectiveScaleRange(
  unit: string,
  visibleSensorsWithData: SensorData[],
  customScaling: { [unit: string]: { min: number; max: number } } | null,
  uomRanges: { uom: string; minValue: number; maxValue: number }[]
): Pick<ScaleGroup, 'min' | 'max' | 'hasCustomScaling' | 'hasRelaySensor'> {
  const unitToMatch = unit?.trim() || '';
  const hasCustomScaling = !!(customScaling && customScaling[unitToMatch]);
  const hasRelaySensor = unitHasRelaySensor(unit, visibleSensorsWithData);

  let min: number | undefined;
  let max: number | undefined;

  if (hasRelaySensor) {
    min = -0.5;
    max = 1.5;
  } else if (hasCustomScaling && customScaling && customScaling[unitToMatch]) {
    min = customScaling[unitToMatch].min;
    max = customScaling[unitToMatch].max;
  } else {
    const uomRange = findUomRange(uomRanges, unitToMatch);
    if (uomRange) {
      min = Math.floor(uomRange.minValue);
      max = Math.ceil(uomRange.maxValue);
    }
  }

  return { min, max, hasCustomScaling, hasRelaySensor };
}

function findScaleGroupByKey(scaleGroups: ScaleGroup[], scaleKey: string): ScaleGroup | undefined {
  for (const group of scaleGroups) {
    if (group.scaleKey === scaleKey) {
      return group;
    }
  }
  return undefined;
}

function buildScaleKey(scaleRange: Pick<ScaleGroup, 'min' | 'max' | 'hasCustomScaling' | 'hasRelaySensor'>): string {
  if (scaleRange.hasRelaySensor) {
    return 'relay-unified--0.5-1.5-false-true';
  }
  return `${scaleRange.min || 'auto'}-${scaleRange.max || 'auto'}-${scaleRange.hasCustomScaling}-${scaleRange.hasRelaySensor}`;
}

export function buildScaleGroups(
  uniqueUnits: string[],
  visibleSensorsWithData: SensorData[],
  customScaling: { [unit: string]: { min: number; max: number } } | null,
  uomRanges: { uom: string; minValue: number; maxValue: number }[]
): ScaleGroup[] {
  const scaleGroups: ScaleGroup[] = [];

  for (const unit of uniqueUnits) {
    const scaleRange = getEffectiveScaleRange(unit, visibleSensorsWithData, customScaling, uomRanges);
    const scaleKey = buildScaleKey(scaleRange);
    const existingGroup = findScaleGroupByKey(scaleGroups, scaleKey);

    if (existingGroup) {
      existingGroup.units.push(unit);
      continue;
    }

    scaleGroups.push({
      scaleKey,
      units: [unit],
      min: scaleRange.min,
      max: scaleRange.max,
      hasCustomScaling: scaleRange.hasCustomScaling,
      hasRelaySensor: scaleRange.hasRelaySensor,
      representativeUnit: unit,
    });
  }

  return scaleGroups;
}

function isSensorNameUnit(unit: string, visibleSensorsWithData: SensorData[]): boolean {
  for (const sensor of visibleSensorsWithData) {
    if (sensor.sensorName === unit && (!sensor.unit || sensor.unit.trim() === '')) {
      return true;
    }
  }
  return false;
}

function getScaleGroupDisplayText(scaleGroup: ScaleGroup, visibleSensorsWithData: SensorData[]): string {
  if (scaleGroup.hasRelaySensor) {
    return 'Binary/Relay';
  }

  const nonEmptyUnits: string[] = [];
  for (const unit of scaleGroup.units) {
    if (!isSensorNameUnit(unit, visibleSensorsWithData)) {
      nonEmptyUnits.push(unit);
    }
  }

  return nonEmptyUnits.length > 0 ? nonEmptyUnits.join(' / ') : '';
}

function usesFixedScaleBounds(scaleGroup: ScaleGroup): boolean {
  return scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor;
}

function getScaleGroupBaseValue(scaleGroup: ScaleGroup): number | undefined {
  if (scaleGroup.hasRelaySensor) {
    return -0.5;
  }
  if (scaleGroup.hasCustomScaling && scaleGroup.min !== undefined) {
    return scaleGroup.min;
  }
  return undefined;
}

function getYAxisLabelXPosition(singleAxisMode: boolean, axisIndex: number): number {
  if (singleAxisMode || axisIndex % 2 === 0) {
    return -10;
  }
  return 15;
}

function applyYAxisScaleBounds(
  yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>,
  scaleGroup: ScaleGroup
): void {
  if (scaleGroup.hasCustomScaling && scaleGroup.min !== undefined && scaleGroup.max !== undefined) {
    yAxis.set('min', scaleGroup.min);
    yAxis.set('max', scaleGroup.max);
    yAxis.set('strictMinMax', false);
  } else if (scaleGroup.hasRelaySensor) {
    yAxis.set('min', -0.5);
    yAxis.set('max', 1.5);
    yAxis.set('strictMinMax', true);
  }
}

function addScaleGroupUnitsToYAxisArray(
  scaleGroup: ScaleGroup,
  yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>,
  yAxisArray: { [key: string]: am5xy.ValueAxis<am5xy.AxisRendererY> }[]
): void {
  for (const unit of scaleGroup.units) {
    yAxisArray.push({ [unit.trim()]: yAxis });
  }
}

function createYAxisForScaleGroup(
  chart: am5xy.XYChart,
  root: am5.Root,
  scaleGroup: ScaleGroup,
  axisIndex: number,
  visibleSensorsWithData: SensorData[],
  yAxisArray: { [key: string]: am5xy.ValueAxis<am5xy.AxisRendererY> }[],
  singleAxisMode: boolean
): void {
  const opposite = singleAxisMode ? false : axisIndex % 2 !== 0;
  const fixedBounds = usesFixedScaleBounds(scaleGroup);
  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      extraTooltipPrecision: 1,
      renderer: am5xy.AxisRendererY.new(root, { opposite }),
      treatZeroAs: fixedBounds ? undefined : 0,
      strictMinMax: fixedBounds,
      min: scaleGroup.min,
      max: scaleGroup.max,
      baseValue: getScaleGroupBaseValue(scaleGroup),
      numberFormat: fixedBounds ? '#.#' : undefined,
      extraMax: fixedBounds ? 0 : undefined,
      extraMin: fixedBounds ? 0 : undefined,
      maxPrecision: fixedBounds ? 1 : undefined,
    }),
  );

  const typedYAxis = yAxis as unknown as am5xy.ValueAxis<am5xy.AxisRendererY>;
  applyYAxisScaleBounds(typedYAxis, scaleGroup);
  yAxis.get('renderer').labels.template.set('fill', am5.color('#fff'));

  const yAxisLabel = am5.Label.new(root, {
    text: getScaleGroupDisplayText(scaleGroup, visibleSensorsWithData),
    fill: am5.color('#fff'),
    rotation: 0,
    centerX: am5.p50,
    centerY: am5.p50,
    x: getYAxisLabelXPosition(singleAxisMode, axisIndex),
    y: -30,
    fontSize: singleAxisMode ? 14 : 12,
    fontWeight: 'bold',
  });
  yAxis.children.push(yAxisLabel);
  addScaleGroupUnitsToYAxisArray(scaleGroup, typedYAxis, yAxisArray);
}

export function createYAxesFromScaleGroups(
  chart: am5xy.XYChart,
  root: am5.Root,
  scaleGroups: ScaleGroup[],
  visibleSensorsWithData: SensorData[]
): { [key: string]: am5xy.ValueAxis<am5xy.AxisRendererY> }[] {
  const yAxisArray: { [key: string]: am5xy.ValueAxis<am5xy.AxisRendererY> }[] = [];

  if (scaleGroups.length === 1) {
    createYAxisForScaleGroup(chart, root, scaleGroups[0], 0, visibleSensorsWithData, yAxisArray, true);
    return yAxisArray;
  }

  for (let index = 0; index < scaleGroups.length; index++) {
    createYAxisForScaleGroup(chart, root, scaleGroups[index], index, visibleSensorsWithData, yAxisArray, false);
  }

  return yAxisArray;
}
